import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import axiosInstance from "../../../api/axiosInstance";

const STATUS_COLORS = {
  draft: "secondary", sent: "info",
  partially_received: "warning", received: "success", cancelled: "danger",
};

export default function ViewPurchaseOrder({ id, onClose }) {
  const [po, setPo] = useState(null);
  const [receiveMode, setReceiveMode] = useState(false);
  const [receiveItems, setReceiveItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchPO = async () => {
    try {
      const res = await axiosInstance.get(`getPurchaseOrderById/${id}`);
      const data = res.data.data;
      setPo(data);
      setReceiveItems((data.items || []).map(item => ({
        item_id: item.id,
        drug_id: item.drug_id,
        drug_name: item.drug_name,
        quantity_ordered: item.quantity_ordered,
        quantity_already_received: item.quantity_received,
        quantity_received: "",
        unit_cost: item.unit_cost,
        batch_number: "",
        expiry_date: "",
      })));
    } catch {
      Swal.fire("Error", "Failed to load purchase order", "error");
    }
  };

  useEffect(() => { fetchPO(); }, [id]);

  const handleReceiveChange = (index, field, value) => {
    const updated = [...receiveItems];
    updated[index][field] = value;
    setReceiveItems(updated);
  };

  const handleReceive = async () => {
    const itemsToSend = receiveItems.filter(i => parseInt(i.quantity_received) > 0);
    if (!itemsToSend.length) return Swal.fire("Validation", "Enter quantity received for at least one item", "warning");

    setSaving(true);
    try {
      await axiosInstance.put(`receivePurchaseOrder/${id}`, { items: itemsToSend });
      Swal.fire("Success", "Stock received and batches created", "success");
      setReceiveMode(false);
      fetchPO();
    } catch {
      Swal.fire("Error", "Failed to receive items", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await axiosInstance.put(`updatePurchaseOrder/${id}`, { ...po, status: newStatus });
      fetchPO();
    } catch {
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  if (!po) return <div className="text-center py-5"><div className="spinner-border" /></div>;

  return (
    <div className="card">
      <div className="card-header bg-white p-3 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <button className="btn btn-sm btn-light me-3" onClick={onClose}>
            <i className="ti ti-arrow-left" /> Back
          </button>
          <h5 className="mb-0">Purchase Order: <strong>{po.po_number}</strong></h5>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className={`badge bg-${STATUS_COLORS[po.status] || "secondary"} fs-6`}>
            {po.status?.replace(/_/g, " ")}
          </span>
          {po.status === "draft" && (
            <button className="btn btn-sm btn-outline-info" onClick={() => handleStatusUpdate("sent")}>
              Mark as Sent
            </button>
          )}
          {["draft", "sent", "partially_received"].includes(po.status) && (
            <button className="btn btn-sm btn-outline-success" onClick={() => setReceiveMode(true)}>
              <i className="ti ti-package-import me-1" /> Receive Items
            </button>
          )}
        </div>
      </div>

      <div className="card-body">
        {/* PO Header Info */}
        <div className="row g-3 mb-4">
          {[
            ["Vendor", po.vendor_name],
            ["Order Date", po.order_date?.slice(0, 10)],
            ["Expected Date", po.expected_date?.slice(0, 10) || "—"],
            ["Received Date", po.received_date?.slice(0, 10) || "—"],
            ["Total Amount", parseFloat(po.total_amount || 0).toFixed(3)],
          ].map(([label, val]) => (
            <div className="col-md-3" key={label}>
              <label className="form-label text-muted">{label}</label>
              <div className="fw-semibold">{val}</div>
            </div>
          ))}
          {po.notes && (
            <div className="col-12">
              <label className="form-label text-muted">Notes</label>
              <div>{po.notes}</div>
            </div>
          )}
        </div>

        {/* Items Table */}
        <h6 className="mb-3">Order Items</h6>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Drug</th>
                <th>Qty Ordered</th>
                <th>Qty Received</th>
                <th>Unit Cost</th>
                <th>Total Cost</th>
                <th>Batch #</th>
                <th>Expiry Date</th>
              </tr>
            </thead>
            <tbody>
              {(po.items || []).map((item, i) => (
                <tr key={item.id}>
                  <td>{i + 1}</td>
                  <td>{item.drug_name}</td>
                  <td>{item.quantity_ordered}</td>
                  <td>
                    <span style={{ color: item.quantity_received >= item.quantity_ordered ? "green" : item.quantity_received > 0 ? "orange" : "inherit" }}>
                      {item.quantity_received}
                    </span>
                  </td>
                  <td>{parseFloat(item.unit_cost || 0).toFixed(3)}</td>
                  <td>{parseFloat(item.total_cost || 0).toFixed(3)}</td>
                  <td>{item.batch_number || "—"}</td>
                  <td>{item.expiry_date?.slice(0, 10) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Receive Items Modal */}
      {receiveMode && (
        <div className="modal fade show" style={{ backgroundColor: "rgba(0,0,0,0.5)", display: "block" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Receive Items — {po.po_number}</h5>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">Enter received quantity, batch number, and expiry date for each item.</p>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Drug</th>
                        <th>Ordered</th>
                        <th>Previously Received</th>
                        <th>Qty Receiving Now <span className="text-danger">*</span></th>
                        <th>Batch Number</th>
                        <th>Expiry Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receiveItems.map((item, i) => (
                        <tr key={i}>
                          <td>{item.drug_name}</td>
                          <td>{item.quantity_ordered}</td>
                          <td>{item.quantity_already_received}</td>
                          <td>
                            <input
                              type="number" min="0"
                              max={item.quantity_ordered - item.quantity_already_received}
                              value={item.quantity_received}
                              onChange={(e) => handleReceiveChange(i, "quantity_received", e.target.value)}
                              className="form-control form-control-sm"
                            />
                          </td>
                          <td>
                            <input
                              type="text" value={item.batch_number}
                              onChange={(e) => handleReceiveChange(i, "batch_number", e.target.value)}
                              placeholder="e.g. B-2024-001"
                              className="form-control form-control-sm"
                            />
                          </td>
                          <td>
                            <input
                              type="date" value={item.expiry_date}
                              onChange={(e) => handleReceiveChange(i, "expiry_date", e.target.value)}
                              className="form-control form-control-sm"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="cancelText me-2" onClick={() => setReceiveMode(false)}>Cancel</button>
                <button type="button" className="bgBtn" onClick={handleReceive} disabled={saving}>
                  {saving ? "Processing..." : "Confirm Receipt"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
